import {
  ApplePay,
  ApplePayPayload,
  ApplePayPaymentRequest,
  ApplePaySession,
  BraintreeError,
} from 'braintree-web';
import { ApplePayProcessorType } from 'hooks/apple-pay/useApplePay';
import { amountCentsForCurrency } from '@tock/money';
import { core } from '@tock/coretypes';

export type ApplePayDetails = Pick<
  ApplePayPaymentRequest,
  'currencyCode' | 'countryCode' | 'supportedNetworks'
>;
export type OnSuccessPromise = (card: core.IPaymentCard) => Promise<unknown>;

interface ApplePayPaymentContact {
  postalCode?: string;
}

interface ApplePayPayment {
  nonceToken?: string;
  token: string;
  billingContact?: ApplePayPaymentContact;
}

interface PaymentAuthorizedEvent {
  payment: ApplePayPayment;
}

interface ValidateMerchantEvent {
  validationURL: string;
}

interface StripeAddress {
  city?: string;
  country?: string;
  line1?: string;
  line2?: string;
  postal_code?: string;
  state?: string;
}

interface StripeBillingDetails {
  address?: StripeAddress;
}

interface StripePaymentMethod {
  id: string;
  billing_details?: StripeBillingDetails;
}

interface StripePaymentMethodPaymentResponse {
  paymentMethod: StripePaymentMethod;
  complete: (status: string) => void;
}

export interface StripeCanMakePayment {
  applePay?: boolean | undefined;
  googlePay?: boolean | undefined;
}

export interface StripePaymentRequest {
  canMakePayment(): Promise<StripeCanMakePayment | null>;
  show(): void;
  update(args: { total: { amount: number; label: string } }): void;
  on(event: 'paymentmethod', handler: (response: StripePaymentMethodPaymentResponse) => void): void;
  on(event: 'cancel', handler: () => void): void;
}

export const APPLE_PAY_DISPLAY_NAME = 'Tock';
export const DEFAULT_ERROR_MESSAGE = 'We encountered an issue loading Apple Pay';
export const RETRY_ERROR_MESSAGE = `${DEFAULT_ERROR_MESSAGE}. Please try again.`;
export const TRY_ANOTHER_METHOD_ERROR_MESSAGE = `${DEFAULT_ERROR_MESSAGE}. Please try using a different payment method.`;

export const getApplePayErrorMessage = (error?: string | BraintreeError): string | undefined => {
  if (!error) {
    return undefined;
  }
  if (typeof error === 'string') {
    return error;
  }

  switch (error.code) {
    /* Occurs when the authorization used is not authorized to process Apple Pay. */
    case 'APPLE_PAY_NOT_ENABLED':
      return TRY_ANOTHER_METHOD_ERROR_MESSAGE;
    /* Occurs when the validationURL option is not passed in. */
    case 'APPLE_PAY_VALIDATION_URL_REQUIRED':
      return TRY_ANOTHER_METHOD_ERROR_MESSAGE;
    /* Occurs when the website domain has not been registered in the Braintree control panel. */
    case 'APPLE_PAY_MERCHANT_VALIDATION_FAILED':
      return TRY_ANOTHER_METHOD_ERROR_MESSAGE;
    /* Occurs when the token option is not passed in. */
    case 'APPLE_PAY_PAYMENT_TOKEN_REQUIRED':
      return RETRY_ERROR_MESSAGE;
    /* Occurs when an unknown network error occurs. */
    case 'APPLE_PAY_TOKENIZATION':
      return RETRY_ERROR_MESSAGE;
    /* Occurs when an unknown network error occurs. */
    case 'APPLE_PAY_MERCHANT_VALIDATION_NETWORK':
      return RETRY_ERROR_MESSAGE;
    default:
      return DEFAULT_ERROR_MESSAGE;
  }
};

export const convertToApplePayProcessingType = (
  processingType: core.PaymentProcessorType | undefined | null
): ApplePayProcessorType | undefined => {
  switch (processingType) {
    case 'BRAINTREE':
      return 'BRAINTREE';
    case 'STRIPE':
      return 'STRIPE';
    default:
      return undefined;
  }
};

export const logApplePayError = (
  processorType: ApplePayProcessorType,
  action: string,
  reason: string | Error | object | undefined
) => {
  const message = `Error during Apple Pay ${action} (${processorType})`;
  window.LOGGER?.error(message, reason);
  // eslint-disable-next-line no-console
  console.error(message, reason);
};

const onBraintreePaymentAuthorized = (
  applePayInstance: ApplePay,
  applePaySession: ApplePaySession,
  onSuccess: OnSuccessPromise,
  resolve: () => void,
  reject: (reason: BraintreeError) => void
) => {
  return (event: PaymentAuthorizedEvent) => {
    applePayInstance.tokenize(
      { token: event.payment.token },
      (error: BraintreeError | undefined, payload: ApplePayPayload | undefined) => {
        if (error) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          applePaySession.completePayment((applePaySession as any).STATUS_FAILURE);
          reject(error);
          return;
        }

        onSuccess({
          nonceToken: payload?.nonce,
          zipCode: event.payment.billingContact?.postalCode,
        })
          .then(() => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            applePaySession.completePayment((applePaySession as any).STATUS_SUCCESS);
            resolve();
          })
          .catch((reason) => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            applePaySession.completePayment((applePaySession as any).STATUS_FAILURE);
            reject(reason);
          });
      }
    );
  };
};

const onBraintreeValidateMerchant = (
  applePayInstance: ApplePay,
  applePaySession: ApplePaySession,
  reject: (reason: BraintreeError) => void
) => {
  return (event: ValidateMerchantEvent) => {
    applePayInstance.performValidation(
      {
        validationURL: event.validationURL,
        displayName: 'Tock',
      },
      (error: BraintreeError | undefined, merchantSession) => {
        if (error) {
          applePaySession.abort();
          reject(error);
          return;
        }

        applePaySession.completeMerchantValidation(merchantSession);
      }
    );
  };
};

export const startBraintreeSession = (
  applePayInstance: ApplePay,
  applePayDetails: ApplePayDetails,
  amountCents: number,
  onSuccess: OnSuccessPromise
) => {
  const paymentRequest = applePayInstance.createPaymentRequest({
    total: {
      label: APPLE_PAY_DISPLAY_NAME,
      amount: `${amountCents / 100}`,
    },
    merchantCapabilities: ['supports3DS'],
    requiredBillingContactFields: ['postalAddress'],
    ...applePayDetails,
  });
  const applePaySession: ApplePaySession = new window.ApplePaySession(3, paymentRequest);

  return new Promise<void>((resolve, reject) => {
    applePaySession.onvalidatemerchant = onBraintreeValidateMerchant(
      applePayInstance,
      applePaySession,
      reject
    );
    applePaySession.onpaymentauthorized = onBraintreePaymentAuthorized(
      applePayInstance,
      applePaySession,
      onSuccess,
      resolve,
      reject
    );
    applePaySession.oncancel = () => reject();

    applePaySession.begin();
  }).catch((reason) => {
    logApplePayError('BRAINTREE', 'payment', reason);
    return Promise.reject(reason);
  });
};

export const getPaymentRequestTotal = (amountCents: number, currencyCode: string) => ({
  label: APPLE_PAY_DISPLAY_NAME,
  amount: amountCentsForCurrency(amountCents, currencyCode),
});

export const createStripePaymentRequest = (
  amountCents: number,
  countryCode: string,
  currencyCode: string
): StripePaymentRequest => {
  return window.stripeV3.paymentRequest({
    country: countryCode,
    currency: currencyCode.toLowerCase(),
    requestPayerName: true,
    requestPayerEmail: true,
    total: getPaymentRequestTotal(amountCents, currencyCode),
  });
};

const onStripePaymentMethod = (
  onSuccess: OnSuccessPromise,
  resolve: () => void,
  reject: (reason: string) => void
) => {
  return (event: StripePaymentMethodPaymentResponse) => {
    onSuccess({
      storedToken: event.paymentMethod.id,
      zipCode: event.paymentMethod.billing_details?.address?.postal_code,
    })
      .then(() => {
        event.complete('success');
        resolve();
      })
      .catch((reason) => {
        event.complete('fail');
        reject(reason);
      });
  };
};

export const startStripeSession = (
  paymentRequest: StripePaymentRequest,
  onSuccess: OnSuccessPromise
) => {
  return new Promise<void>((resolve, reject) => {
    paymentRequest.on('paymentmethod', onStripePaymentMethod(onSuccess, resolve, reject));
    paymentRequest.on('cancel', () => reject());

    paymentRequest.show();
  }).catch((reason) => {
    logApplePayError('STRIPE', 'payment', reason);
    return Promise.reject(reason);
  });
};
